import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import useAssignedToChallan from '../hooks/useAssignedToChallan';
import AssignedToChallanFilter from '../components/assigned-to-challan/AssignedToChallanFilter';
import PendingSummaryCards from '../components/assigned-to-challan/PendingSummaryCards';
import PendingBacklogTable from '../components/assigned-to-challan/PendingBacklogTable';
import DurationView from '../components/assigned-to-challan/DurationView';
import Pagination from '../components/common/Pagination';
import userService from '../services/userService';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { useTranslation } from 'react-i18next';
import { toISTDateString } from '../utils/dates';

const DEFAULT_PAGE_LIMIT = 50;

const getDefaultDateRange = () => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 7);
    const toISO = toISTDateString(today);
    const fromISO = toISTDateString(from);
    return { from: fromISO, to: toISO };
};

const AssignedToChallanPage = () => {
    const { t } = useTranslation();
    const TABS = [
        { id: 'pending',  label: t('assignedToChallan.tabs.liveBacklog') },
        { id: 'duration', label: t('assignedToChallan.tabs.durationStats') },
    ];
    const { userRole } = useRole();
    const isAdminOrSuper = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;

    const {
        pendingItems, pendingSummary, pendingTotal,
        duration, fetching, error,
        fetchPending, fetchDuration,
    } = useAssignedToChallan();

    const [supervisors, setSupervisors] = useState([]);
    const [supervisorsLoading, setSupervisorsLoading] = useState(false);
    const [selectedSupervisorId, setSelectedSupervisorId] = useState('');

    const [dateRange, setDateRange] = useState(getDefaultDateRange);
    const [activeTab, setActiveTab] = useState('pending');
    const [unitType, setUnitType] = useState('');
    const [groupBy, setGroupBy] = useState('none');
    const [currentPage, setCurrentPage] = useState(1);

    const isTabEffectReady = useRef(false);

    const filterRef = useRef({});
    filterRef.current = { selectedSupervisorId, dateRange, unitType, groupBy };

    const fetchForTab = useCallback(async (tab, page = 1) => {
        const { selectedSupervisorId: sid, dateRange: dr, unitType: ut, groupBy: gb } = filterRef.current;
        const base = { from_date: dr.from, to_date: dr.to };
        if (sid) base.supervisor_id = sid;

        if (tab === 'pending') {
            const p = { ...base, limit: DEFAULT_PAGE_LIMIT, skip: (page - 1) * DEFAULT_PAGE_LIMIT };
            if (ut) p.unit_type = ut;
            await fetchPending(p);
        } else if (tab === 'duration') {
            const p = { ...base, group_by: gb };
            if (ut) p.unit_type = ut;
            await fetchDuration(p);
        }
    }, [fetchPending, fetchDuration]);

    // Load supervisors on mount, then initial fetch
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            setSupervisorsLoading(true);
            try {
                if (isAdminOrSuper) {
                    const res = await userService.getDeliverySupervisorsStatus({ activeOnly: true, limit: 500 });
                    if (cancelled) return;
                    const list = res.success ? (Array.isArray(res.data) ? res.data : []) : [];
                    setSupervisors(list);
                }
                await fetchForTab('pending', 1);
            } finally {
                if (!cancelled) {
                    setSupervisorsLoading(false);
                    isTabEffectReady.current = true;
                }
            }
        };
        init();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch when tab changes
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Re-fetch on pagination change (backlog only)
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        if (activeTab === 'pending') fetchForTab('pending', currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleApply = () => {
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
    };

    const totalPages = Math.ceil(pendingTotal / DEFAULT_PAGE_LIMIT) || 1;
    const showPagination = activeTab === 'pending';

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={5}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box sx={{
                        bgcolor: '#189AB4', borderRadius: 2, width: 40, height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                    }}>
                        <FontAwesomeIcon icon={faHourglassHalf} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        {t('assignedToChallan.page.title')}
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
                    {t('assignedToChallan.page.subtitle')}
                </Typography>
            </Box>

            {/* Filter bar */}
            <AssignedToChallanFilter
                view={activeTab}
                supervisors={supervisors}
                supervisorsLoading={supervisorsLoading}
                selectedSupervisorId={selectedSupervisorId}
                onSupervisorChange={setSelectedSupervisorId}
                fromDate={dateRange.from}
                toDate={dateRange.to}
                onFromDateChange={(v) => setDateRange((p) => ({ ...p, from: v }))}
                onToDateChange={(v) => setDateRange((p) => ({ ...p, to: v }))}
                unitType={unitType}
                onUnitTypeChange={setUnitType}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                onSubmit={handleApply}
                loading={fetching}
            />

            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: '2px solid #f3f4f6', flexWrap: 'wrap' }}>
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '10px 20px', border: 'none',
                            borderBottom: activeTab === tab.id ? '3px solid #189AB4' : '3px solid transparent',
                            backgroundColor: 'transparent', cursor: 'pointer', fontSize: 14,
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            color: activeTab === tab.id ? '#189AB4' : '#6b7280',
                            transition: 'all 0.15s', marginBottom: -2,
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </Box>

            {/* Error */}
            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {/* Initial loading spinner */}
            {fetching && activeTab === 'pending' && !pendingSummary && (
                <Box display="flex" justifyContent="center" p={10}>
                    <CircularProgress size={40} thickness={4} sx={{ color: '#189AB4' }} />
                </Box>
            )}

            {/* Backlog tab */}
            {activeTab === 'pending' && (
                <>
                    <PendingSummaryCards summary={pendingSummary} />
                    <PendingBacklogTable items={pendingItems} loading={fetching} />
                    {showPagination && pendingItems?.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={pendingTotal}
                            onPageChange={setCurrentPage}
                            loading={fetching}
                            totalLabel={t('assignedToChallan.pagination.totalLabels.subOrders')}
                            pageSize={DEFAULT_PAGE_LIMIT}
                        />
                    )}
                </>
            )}

            {/* Duration tab */}
            {activeTab === 'duration' && (
                <DurationView duration={duration} loading={fetching} />
            )}
        </Container>
    );
};

export default AssignedToChallanPage;
